import type { APIManager } from './apiManager';
import { type Logger } from '../common/logger';
import Mutex from '../common/mutex';
import { isNullish, withError } from '../common/types';
import type {
  AppSpecificAttributeGetters,
  DeviceAttributeGetters,
  OrionRequestAttributes,
  SegmentationsToForce,
} from '../model/configuration';
import type { Identity, OrionSegmentations } from '../model/identityConfiguration';
import type { PersistenceManager } from '../model/persistenceManager';

export type EntityManager = Awaited<ReturnType<typeof createEntityManager>>;

export type EntityManagerConfig<Settings extends Record<string, unknown>> = {
  apiManager: APIManager;
  persistenceManager: PersistenceManager<Settings>;
  logger: Logger;
  defaultSettings: Settings;
  defaultSegmentations: OrionSegmentations;
  deviceAttributeGetters: DeviceAttributeGetters;
  appSpecificAttributeGetters: AppSpecificAttributeGetters;
  userIdGenerator: (requestAttributes: OrionRequestAttributes) => Promise<string>;
  shouldReturnDefaultsImmediately: boolean;
  identityCacheTtlInMs: number;
};

/**
 * Entity manager is responsible for retrieving from remote, and storing locally Orion entities.
 */
export default async function createEntityManager<Settings extends Record<string, unknown>>(
  config: EntityManagerConfig<Settings>,
) {
  const settingsFetchMutex = new Mutex();

  // Merge the in memory identity with the default values.
  // This prevents crashes when applying changes to the remote identity. (eg: removing a setting key)
  const mergeWithDefaults = (identity: Identity<Settings> | null) => {
    return {
      isDefaultIdentity: identity?.isDefaultIdentity ?? true,
      settings: {
        ...config.defaultSettings,
        ...(identity?.settings ?? {}),
      },
      segmentations: {
        ...config.defaultSegmentations,
        ...(identity?.segmentations ?? {}),
      },
      settingsHash: identity?.settingsHash ?? '',
    };
  };

  // Given a record of getters run all of them and return a record of the results.
  // If a getter returns undefined or is undefined, the key is not included in the result.
  const runGetters = async (
    getters: Record<string, () => Promise<unknown>>,
  ): Promise<Record<string, unknown>> => {
    const values: Record<string, unknown> = {};

    for (const getterName in getters) {
      const p = getters[getterName];

      if (p !== undefined) {
        const value = await p();
        if (value !== undefined) {
          values[getterName] = value;
        }
      }
    }

    return values;
  };

  const fetchAndCacheRemoteSettings = async (
    entityId: string,
    requestAttributes: OrionRequestAttributes,
  ) => {
    config.logger.debug(`Requesting latest settings from remote at time: ${new Date().getTime()}`);
    const configurationResponse = await withError(
      config.apiManager.requestSettingsAndSegmentations(requestAttributes),
    );

    if (configurationResponse.err) {
      config.logger.debug(configurationResponse.err.message);
      return mergeWithDefaults(null);
    }

    if (isNullish(configurationResponse.data)) {
      return mergeWithDefaults(null);
    }

    try {
      const settingsAndSegmentationsResponse = configurationResponse.data;
      const identity: Identity<Settings> = {
        isDefaultIdentity: false,
        settings: settingsAndSegmentationsResponse.settings as Settings,
        segmentations: settingsAndSegmentationsResponse.segmentations,
        settingsHash: settingsAndSegmentationsResponse.settings_hash,
      };
      const identityWithDefaults = mergeWithDefaults(identity);
      await config.persistenceManager.saveIdentity(identityWithDefaults, entityId);
      return identityWithDefaults;
    } catch (e) {
      config.logger.error('Failed to store remote identity', e);
      return mergeWithDefaults(null);
    }
  };

  const fetchLastValidIdentityFromCache = async (
    entityId: string,
  ): Promise<Identity<Settings> | null> => {
    const maybeCachedIdentity = await config.persistenceManager.loadIdentity(entityId);

    // Cache miss
    if (!maybeCachedIdentity) {
      return null;
    }

    // Cache TTL expired
    const { identity, updatedAt } = maybeCachedIdentity;
    if (updatedAt + config.identityCacheTtlInMs < new Date().getTime()) {
      return null;
    }

    return identity;
  };

  const fetchIdentity = async (): Promise<Identity<Settings>> => {
    if (config.shouldReturnDefaultsImmediately) {
      return {
        isDefaultIdentity: true,
        settings: config.defaultSettings,
        segmentations: config.defaultSegmentations,
        settingsHash: '',
      };
    }

    const [deviceAttributes, appSpecificAttributes] = await Promise.all([
      runGetters(config.deviceAttributeGetters),
      runGetters(config.appSpecificAttributeGetters),
    ]);
    const requestAttributes = { ...deviceAttributes, ...appSpecificAttributes };
    const entityId = await config.userIdGenerator(requestAttributes);

    try {
      const cachedIdentity = await fetchLastValidIdentityFromCache(entityId);
      if (cachedIdentity) {
        return mergeWithDefaults(cachedIdentity);
      }
      return await fetchAndCacheRemoteSettings(entityId, requestAttributes);
    } catch (e) {
      config.logger.error('Failed to fetch identity', e);
      return mergeWithDefaults(null);
    }
  };

  const fetchIdentityWithMutex = async () => {
    return await settingsFetchMutex.run(async () => {
      return await fetchIdentity();
    });
  };

  return {
    isSecretMenuEligible: async () => {
      const identity = await fetchIdentityWithMutex();
      return Boolean(identity?.settings?.['is_spooner_device']);
    },
    /**
     * Get all Orion experiments
     */
    getAllExperiments: async () => {
      const [deviceAttributes, appSpecificAttributes] = await Promise.all([
        runGetters(config.deviceAttributeGetters),
        runGetters(config.appSpecificAttributeGetters),
      ]);
      return await config.apiManager.getAllExperiments({
        ...deviceAttributes,
        ...appSpecificAttributes,
      });
    },
    /**
     * Force segment user inside a specific segment in the given experiment
     */
    setExperimentSegmentation: (
      experimentName: string,
      segmentIndex: number | undefined,
      segmentation: { idName: string; idValue: string },
    ) => {
      return config.apiManager.forceExperimentSegmentation(
        experimentName,
        segmentIndex,
        segmentation,
      );
    },
    /**
     * Force segment user inside specific segments in the given experiments
     */
    setExperimentSegmentations: (segmentations: SegmentationsToForce) => {
      return config.apiManager.forceExperimentSegmentations(segmentations);
    },
    /**
     * Get the current device attributes
     */
    getDeviceAttributes: async () => {
      return await runGetters(config.deviceAttributeGetters);
    },
    /**
     * Get the current user attributes
     */
    getUserAttributes: async () => {
      return await runGetters(config.appSpecificAttributeGetters);
    },
    /**
     * Set the segmentation state for the user.
     * If exclude_from_segmentation=True, remove the identity from ALL experiments and stop it from being segmented into any other experiments until included in segmentation again.
     * If exclude_from_segmentation=False, make the identity segmental again.
     */
    excludeFromSegmentation: (excludeFromSegmentation: boolean, segmentationIdName: string) => {
      return config.apiManager.setSegmentationState(excludeFromSegmentation, segmentationIdName);
    },
    /**
     * Returns the latest Orion identity fetched, merged with default values.
     * The call is mutually exclusive, meaning that if multiple calls are made at the same time, only one will fetch the identity from the remote.
     * The other calls will wait for the first call to finish and return the same cached identity.
     *
     * If the stored identity is not available
     */
    getIdentity: fetchIdentityWithMutex,
  };
}
